/**
 * iOS-Style Button Styles (Web - Flat Design)
 * Apple iOS 17+ web design language - Flat style, all colors from theme
 *
 * Features:
 * - Flat colors (no gradients)
 * - Rounded corners
 * - Smooth transitions
 * - Press states with scale
 * - Clean typography
 * - All colors from theme
 */

import { adjustOpacity } from './utils';

// Helper function to get theme colors
const getThemeColors = theme => ({
  primary: theme?.palette?.primary?.main || '#007AFF', // iOS System Blue
  primaryLight: theme?.palette?.primary?.light || '#5AC8FA', // iOS Light Blue
  primaryDark: theme?.palette?.primary?.dark || '#0051D5', // Darker iOS Blue
  secondary: theme?.palette?.secondary?.main || '#8E8E93', // iOS Secondary Gray
  // Prefer explicit accent from theme; otherwise fall back to primary to avoid hardcoded purple
  accent:
    theme?.palette?.accent?.main ||
    theme?.palette?.secondary?.main ||
    theme?.palette?.primary?.main ||
    '#007AFF',
  error: theme?.palette?.error?.main || '#FF3B30', // iOS Red
  success: theme?.palette?.success?.main || '#34C759', // iOS Green
  textPrimary: theme?.palette?.text?.primary || '#000000',
  textSecondary: theme?.palette?.text?.secondary || '#8E8E93', // iOS Secondary Gray
  background: theme?.palette?.background?.paper || '#FFFFFF',
  divider: theme?.palette?.divider || '#E5E5EA', // iOS Divider Gray
  iconDefault: '#8E8E93', // Default icon color (iOS Secondary Gray)
  iconHover: '#007AFF', // Icon hover color (iOS System Blue)
});

export const createIOSButtonStyles = theme => {
  const colors = getThemeColors(theme);

  return {
    // Primary Button (iOS System Blue style - Flat)
    primary: {
      // Base styles - Flat color from theme
      background: colors.primary, // Flat, no gradient
      color: '#FFFFFF',
      borderRadius: '8px',
      padding: '6px 14px',
      fontSize: '0.8125rem', // 13px
      fontWeight: 600,
      letterSpacing: '-0.01em',
      border: 'none',
      boxShadow: 'none', // Flat - no shadow
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      minHeight: '32px',

      // Hover state
      '&:hover': {
        background: colors.primaryDark, // Darker shade from theme
        transform: 'translateY(-1px)',
      },

      // Active/Press state (iOS press effect)
      '&:active': {
        transform: 'scale(0.97)',
        background: colors.primaryDark,
      },

      // Focus state
      '&:focus-visible': {
        outline: `2px solid ${colors.primary}`,
        outlineOffset: '2px',
      },

      // Disabled state
      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
        transform: 'none',
      },
    },

    // Secondary Button (iOS outlined style - Flat, muted indigo)
    secondary: {
      // Base styles - Muted indigo (not gray, to avoid confusion with disabled)
      background: 'transparent',
      color: '#6B7A8E', // Muted indigo-blue (clearly not gray, clearly enabled)
      borderRadius: '8px',
      padding: '6px 14px',
      fontSize: '0.8125rem',
      fontWeight: 500, // Lighter weight for secondary
      letterSpacing: '-0.01em',
      border: `1px solid #B8C5D1`, // Muted indigo border
      boxShadow: 'none', // Flat
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      minHeight: '32px',

      // Hover state
      '&:hover': {
        background: adjustOpacity('#6B7A8E', 0.1), // 10% muted indigo background
        borderColor: '#5A6B7D', // Darker indigo border
        color: '#5A6B7D', // Darker indigo text on hover
        transform: 'translateY(-1px)',
      },

      // Active/Press state
      '&:active': {
        transform: 'scale(0.97)',
        background: adjustOpacity('#6B7A8E', 0.15),
      },

      // Focus state
      '&:focus-visible': {
        outline: `2px solid ${colors.primary}`,
        outlineOffset: '2px',
      },

      // Disabled state (gray - clearly different from enabled)
      '&:disabled': {
        opacity: 0.4,
        color: '#CBD5E1', // Light gray for disabled
        borderColor: '#E2E8F0', // Light gray border for disabled
        cursor: 'not-allowed',
        transform: 'none',
      },
    },

    // Tertiary Button (iOS text button style - Flat)
    tertiary: {
      // Base styles - Colors from theme
      background: 'transparent',
      color: colors.primary,
      borderRadius: '8px',
      padding: '6px 12px',
      fontSize: '0.8125rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      border: 'none',
      boxShadow: 'none',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      minHeight: '32px',

      // Hover state
      '&:hover': {
        background: adjustOpacity(colors.primary, 0.08), // 8% opacity
        color: colors.primaryDark,
      },

      // Active/Press state
      '&:active': {
        transform: 'scale(0.97)',
        background: adjustOpacity(colors.primary, 0.15), // 15% opacity
      },

      // Focus state
      '&:focus-visible': {
        outline: `2px solid ${colors.primary}`,
        outlineOffset: '2px',
      },
    },

    // Destructive Button (iOS red style - Flat)
    destructive: {
      // Base styles - Error color from theme
      background: colors.error, // Flat, no gradient
      color: '#FFFFFF',
      borderRadius: '8px',
      padding: '6px 14px',
      fontSize: '0.8125rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      border: 'none',
      boxShadow: 'none', // Flat
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      minHeight: '32px',

      // Hover state
      '&:hover': {
        background: theme?.palette?.error?.dark || '#DC2626',
        transform: 'translateY(-1px)',
      },

      // Active/Press state
      '&:active': {
        transform: 'scale(0.97)',
        background: theme?.palette?.error?.dark || '#DC2626',
      },

      // Focus state
      '&:focus-visible': {
        outline: `2px solid ${colors.error}`,
        outlineOffset: '2px',
      },
    },

    // Small size variant (for template cards)
    small: {
      padding: '4px 12px',
      fontSize: '0.75rem', // 12px
      minHeight: '28px',
      borderRadius: '6px',
    },

    // Large size variant
    large: {
      padding: '14px 28px',
      fontSize: '1rem', // 16px
      minHeight: '52px',
      borderRadius: '12px',
    },

    // Icon Button Styles (for secondary action icons)
    iconButton: {
      // Default state - muted indigo (not gray, to avoid confusion with disabled)
      color: '#6B7A8E', // Muted indigo-blue (clearly not gray, clearly enabled)
      backgroundColor: 'transparent',
      borderRadius: '6px',
      padding: '6px',
      minWidth: '32px',
      minHeight: '32px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

      '&:hover': {
        backgroundColor: adjustOpacity('#6B7A8E', 0.1),
        color: '#5A6B7D', // Darker indigo on hover
      },

      '&:active': {
        transform: 'scale(0.95)',
        backgroundColor: adjustOpacity('#6B7A8E', 0.15),
      },

      // Disabled state (gray - clearly different from enabled)
      '&:disabled': {
        opacity: 0.4,
        color: '#CBD5E1', // Light gray for disabled
        cursor: 'not-allowed',
      },
    },

    // Icon Button - Analytics (subtle blue)
    iconButtonAnalytics: {
      color: colors.iconDefault,
      backgroundColor: 'transparent',
      borderRadius: '6px',
      padding: '6px',
      minWidth: '32px',
      minHeight: '32px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

      '&:hover': {
        backgroundColor: adjustOpacity(colors.primary, 0.1),
        color: colors.primary, // iOS System Blue
      },

      '&:active': {
        transform: 'scale(0.95)',
        backgroundColor: adjustOpacity(colors.primary, 0.15),
      },
    },

    // Icon Button - Destructive (red on hover)
    iconButtonDestructive: {
      color: '#6B7A8E', // Muted indigo by default (same as other icons)
      backgroundColor: 'transparent',
      borderRadius: '6px',
      padding: '6px',
      minWidth: '32px',
      minHeight: '32px',
      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

      '&:hover': {
        backgroundColor: adjustOpacity(colors.error, 0.1), // Red background on hover
        color: colors.error, // Red color on hover (destructive action)
      },

      '&:active': {
        transform: 'scale(0.95)',
        backgroundColor: adjustOpacity(colors.error, 0.15),
        color: colors.error,
      },
    },
  };
};

// Default export for backward compatibility (uses default theme colors)
export const iOSButtonStyles = createIOSButtonStyles(null);

export default iOSButtonStyles;
